/**
 * The chat list: filters, then rows, newest first.
 *
 * ── Order is the source's rule, drawn here ──────────────────────────────────
 * Pinned first, the self-note first within pinned, then most recent. That is
 * `DataSource::conversations()`'s documented order in the desktop app, and the
 * list draws it rather than inventing a second one — the moment the server
 * sorts, a view that sorted differently disagrees with it.
 *
 * ── Three filters, and archived is not one of them ──────────────────────────
 * All, unread, group chats. Archiving is a way of saying "not in my list", so an
 * archived conversation is out of all three; the one place it still appears is
 * a group at the foot of All, which is what makes unarchiving reachable
 * without a fourth tab for a shelf most people never open.
 *
 * The unread count lives on the row and nowhere else. Chrome carries no counts.
 */

import React, { useEffect, useState } from 'react';
import { ds } from '../ds';
import { useRouter } from '../router';
import { useT } from '../i18n';
import { store, useStore } from '../lib/relay/store';
import { ConversationRow } from '../components/chat/ConversationRow';
import { findPerson, isGroupChat, startChat } from '../components/chat/groups';
import { Empty } from '../components/Shell';
import { onChromeIntent, takeChromeIntent } from '../state/chrome';
import type { Conversation, Person } from '../lib/relay/types';
import '../components/chat/chat.css';

type Filter = 'all' | 'unread' | 'groups';

/** Pinned first, the self-note first within pinned, then most recent. */
function inOrder(list: Conversation[]): Conversation[] {
  const rank = (c: Conversation) => (c.pinned ? (c.kind === 'self' ? 0 : 1) : 2);
  return [...list].sort((a, b) => rank(a) - rank(b) || b.lastAt - a.lastAt);
}

function keep(c: Conversation, filter: Filter): boolean {
  if (filter === 'unread') return c.unread > 0;
  if (filter === 'groups') return isGroupChat(c);
  return true;
}

/** A new chat: one person, or several.
 *
 *  People are added one at a time by exact username or email address, which is
 *  `RelayStore.lookup` and the server behind it: a prefix search over the
 *  accounts table would be an enumeration service. One person makes a direct
 *  chat; two or more make a group chat, which needs a name. */
function NewChatDialog({ onClose }: { onClose: () => void }) {
  const { Button, Dialog, Input, Tag } = ds();
  const t = useT();
  const { navigate } = useRouter();
  const [people, setPeople] = useState<Person[]>([]);
  const [who, setWho] = useState('');
  const [title, setTitle] = useState('');
  const [whoError, setWhoError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const group = people.length >= 2;

  /** Whoever is in the field, added. The people there are now, or null with the
   *  reason drawn under the field. */
  const take = async (): Promise<Person[] | null> => {
    const typed = who.trim();
    if (!typed) return people;
    const found = await findPerson(store, typed);
    if ('refusal' in found) {
      setWhoError(found.refusal === 'self' ? t('chats.new.self') : t('chats.new.notfound'));
      return null;
    }
    const next = people.some((p) => p.id === found.person.id) ? people : [...people, found.person];
    setPeople(next);
    setWho('');
    return next;
  };

  const forget = () => {
    setWhoError('');
    setTitleError('');
    setProblem('');
  };

  const add = async () => {
    if (busy) return;
    setBusy(true);
    forget();
    try {
      await take();
    } finally {
      setBusy(false);
    }
  };

  // Whatever happens, the dialog is let go of: a request that failed is a
  // sentence on screen, never a Start button that stays disabled.
  const start = async () => {
    if (busy) return;
    setBusy(true);
    forget();
    try {
      const chosen = await take();
      if (!chosen) return;
      const result = await startChat(store, chosen, title);
      if ('id' in result) {
        onClose();
        navigate(`/c/${result.id}`);
      } else if (result.refusal === 'empty') {
        setWhoError(t('chats.new.none'));
      } else if (result.refusal === 'unnamed') {
        setTitleError(t('chats.new.unnamed'));
      } else {
        setProblem(result.message ?? t('common.problem'));
      }
    } finally {
      setBusy(false);
    }
  };

  const enter =
    (then: () => Promise<void>) => (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
      e.preventDefault();
      void then();
    };

  return (
    <Dialog
      open
      position="fixed"
      title={t('chats.new')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void start()} disabled={busy}>
            {t('chats.new.start')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--space-12)' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-8)' }}>
            <Input
              label={t('chats.new.who')}
              value={who}
              autoComplete="off"
              autoFocus
              error={whoError || undefined}
              style={{ flex: 1 }}
              onChange={(e) => {
                setWho(e.target.value);
                setWhoError('');
              }}
              onKeyDown={enter(add)}
            />
            <Button variant="secondary" onClick={() => void add()} disabled={busy || !who.trim()}>
              {t('common.add')}
            </Button>
          </div>
          {people.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-6)',
                marginTop: 'var(--space-8)'
              }}
            >
              {people.map((p) => (
                <Tag key={p.id} onRemove={() => setPeople((list) => list.filter((x) => x.id !== p.id))}>
                  {p.name}
                </Tag>
              ))}
            </div>
          )}
        </div>

        {group && (
          <Input
            label={t('chats.new.name')}
            value={title}
            autoComplete="off"
            error={titleError || undefined}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            onKeyDown={enter(start)}
          />
        )}

        {problem && (
          <div role="alert" style={{ fontSize: 13, color: 'var(--danger-500)' }}>
            {problem}
          </div>
        )}
      </div>
    </Dialog>
  );
}

const FILTERS: Filter[] = ['all', 'unread', 'groups'];

/** What the filter field looks at: the title and the line under it, which is
 *  what somebody typing a name or half a sentence is looking for. Not the whole
 *  log — a list that searched message bodies would answer with rows whose
 *  preview does not contain what was typed. */
function matches(c: Conversation, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    c.title.toLowerCase().includes(q) || (c.preview ?? '').toLowerCase().includes(q)
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="rl-eyebrow">{children}</div>;
}

export function ChatsList() {
  const { Button, Input } = ds();
  const t = useT();
  const { path } = useRouter();
  const conversations = useStore((s) => s.conversations());
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);

  // The title bar's New chat, which is pressed from wherever somebody happens
  // to be: it navigates here and leaves the intent waiting, and a press while
  // this list is already open arrives through the subscription.
  useEffect(() => {
    if (takeChromeIntent('new-chat')) setComposing(true);
    return onChromeIntent((intent) => {
      if (intent === 'new-chat') setComposing(true);
    });
  }, []);

  const shown = inOrder(
    conversations.filter((c) => !c.archived && keep(c, filter) && matches(c, query))
  );
  const pinned = shown.filter((c) => c.pinned);
  const rest = shown.filter((c) => !c.pinned);
  const archived =
    filter === 'all'
      ? inOrder(conversations.filter((c) => c.archived && matches(c, query)))
      : [];

  const empty =
    filter === 'unread'
      ? { title: t('chats.empty.unread') }
      : filter === 'groups'
        ? { title: t('chats.empty.groups') }
        : { title: t('chats.empty.title'), body: t('chats.empty.body') };

  const row = (c: Conversation) => (
    <ConversationRow key={c.id} conversation={c} active={path === `/c/${c.id}`} />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* No title over the tabs: the rail already says which surface this is,
          and the app's list starts with them. New chat lives in the title bar
          now, which is where the app keeps it too. */}
      <div className="rl-listhead">
        <div className="rl-tabs" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              className="rl-tab"
              data-active={filter === f}
              aria-selected={filter === f}
              onClick={() => setFilter(f)}
            >
              {t(`chats.filter.${f}`)}
            </button>
          ))}
        </div>
        <div className="rl-filters">
          <Input
            size="sm"
            icon="Search"
            value={query}
            placeholder={t('chats.search')}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setQuery(e.target.value)
            }
          />
        </div>
      </div>

      {shown.length === 0 && archived.length === 0 ? (
        <Empty
          title={empty.title}
          {...(empty.body ? { body: empty.body } : {})}
          action={<Button onClick={() => setComposing(true)}>{t('chats.new')}</Button>}
        />
      ) : (
        <div className="mv-scroll" style={{ flex: 1, padding: 'var(--space-8) 0' }}>
          {pinned.length > 0 && <Eyebrow>{t('chats.pinned')}</Eyebrow>}
          {pinned.map(row)}
          {rest.map(row)}
          {archived.length > 0 && <Eyebrow>{t('chats.archived')}</Eyebrow>}
          {archived.map(row)}
        </div>
      )}

      {composing && <NewChatDialog onClose={() => setComposing(false)} />}
    </div>
  );
}
